// Library Methods
import axios from "axios";

// Utils
import { config, webServiceUrl } from "src/utils/services/servicesHelper";

// Types
import {
  AddEditPatientType,
  EditPatientInterventionType,
  PatientInterventionType,
  PatientType,
} from "./types";

const getPatients = async (
  accessToken: string,
  clinicId: string,
  signal: AbortSignal
): Promise<Array<PatientType>> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/patients`,
      config(accessToken, signal)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const getPatientById = async (
  accessToken: string | null,
  clinicId: string,
  patientId: string,
  signal: AbortSignal
): Promise<PatientType> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}`,
      config(accessToken, signal)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const addPatient = async (
  accessToken: string,
  clinicId: string,
  patientData: AddEditPatientType
): Promise<PatientType> => {
  try {
    const { data } = await axios.post(
      `${webServiceUrl}/clinics/${clinicId}/patients`,
      patientData,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const updatePatient = async (
  accessToken: string,
  clinicId: string,
  patientId: string,
  patientData: AddEditPatientType
): Promise<PatientType> => {
  try {
    const { data } = await axios.put(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}`,
      patientData,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const deletePatients = async (
  accessToken: string,
  clinicId: string,
  patientIds: Array<string>
): Promise<void> => {
  try {
    await Promise.all(
      patientIds.map((patientId) =>
        axios.delete(
          `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}`,
          config(accessToken)
        )
      )
    );
  } catch (error) {
    throw error;
  }
};

const getTimelineInterventions = async (
  accessToken: string,
  clinicId: string,
  patientId: string,
  signal: AbortSignal
): Promise<PatientInterventionType> => {
  try {
    const { data } = await axios.get(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/interventions`,
      config(accessToken, signal)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const addTimelineIntervention = async (
  accessToken: string,
  clinicId: string,
  patientId: string,
  interventionData: any
): Promise<any> => {
  try {
    const { data } = await axios.post(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/interventions`,
      interventionData,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const updateTimelineIntervention = async (
  accessToken: string,
  clinicId: string,
  patientId: string,
  interventionId: string,
  interventionData: EditPatientInterventionType
): Promise<PatientInterventionType> => {
  try {
    const { data } = await axios.put(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/interventions/${interventionId}`,
      interventionData,
      config(accessToken)
    );
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const deleteTimelineIntervention = async (
  accessToken: string,
  clinicId: string,
  patientId: string,
  interventionId: string
): Promise<void> => {
  try {
    const { data } = await axios.delete(
      `${webServiceUrl}/clinics/${clinicId}/patients/${patientId}/interventions/${interventionId}`,
      config(accessToken)
    );

    return data?.data;
  } catch (error) {
    throw error;
  }
};

export {
  getPatients,
  getPatientById,
  addPatient,
  updatePatient,
  deletePatients,

  // interventions
  getTimelineInterventions,
  addTimelineIntervention,
  updateTimelineIntervention,
  deleteTimelineIntervention,
};
